// ============================================
// MOON MAP - customizer UI (Alpine)
// ============================================
//
// The sidebar, the preview's floating controls and the modals, bound to the
// state in moon-myjs.js (window.MoonCore / Alpine.store('moon')).
//
// Markup the moon section owns is wired declaratively: tiles carry
// `@click="pickX($el)"` and `:class` bindings against the store, modals are
// driven by flags in Alpine.store('moonUi'). Markup that comes from shared
// snippets (step nav, footer, font picker, toolbar) is bound here with
// Alpine.effect / delegated listeners instead, so the snippets stay generic.
//
// Loads after moon-myjs.js and before alpine.min.js (document order =
// execution order for deferred scripts), so the Alpine component and stores
// are registered before Alpine starts.

(function () {
'use strict';
if (window.__MOON_HEADLESS) return;

var C = window.MoonCore;
if (!C) { console.error("[moon-ui] moon-myjs.js did not load"); return; }

function q(sel, root) { return (root || document).querySelector(sel); }
function qa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
function S() { return C.store(); }
function UI() { return window.Alpine ? window.Alpine.store("moonUi") : uiState; }
function fire(el, type) { if (el) el.dispatchEvent(new Event(type, { bubbles: true })); }
function claimActive() { return !!(window.SkylitClaim && window.SkylitClaim.active); }

// UI-only state: which modal is open, step-badge progress.
var uiState = {
    infoOpen: false,
    saveOpen: false,
    successOpen: false,
    successEmail: "",
    successNewsletter: false,
    emojiOpen: false,
    emojiCategory: "smileys",
    inspOpen: false,
    inspCategory: "anniversary",
    inspSelected: null,
    welcomeOpen: false,
    dateOpen: false,
    timeOpen: false,
    // Step badges: steps the customer has opened, and steps opened while the
    // Moment step was already complete.
    visited: { 1: true },
    completed: { 1: true },
    // Bumped when the claim lookup resolves, so labels re-derive.
    claimTick: 0,
};

// ============================================
// PICKER ACTIONS (called from the markup)
// ============================================
var actions = {
    pickComposition: function (el) { C.setComposition(el.getAttribute("data-composition") || "classic"); },
    pickTexture: function (el) {
        var t = el.getAttribute("data-texture");
        if (t) C.setTexture(t);
    },
    pickBgColor: function (el) {
        qa('input[name="background-img"]').forEach(function (i) { i.checked = false; });
        C.setBackgroundColor(el.getAttribute("data-bg"), el.getAttribute("data-fg"));
    },
    // The image tiles are <label for=radio>; the radio's change event lands
    // here. value = high-res URL for the print, the label's image data-preview
    // = the web-resolution background (its src is a 64px swatch thumbnail).
    pickBgImage: function (input) {
        if (!input || !input.checked) return;
        var img = q("label[for='" + input.id + "'] img");
        var preview = (img && (img.getAttribute("data-preview") || img.getAttribute("src"))) || input.value;
        C.setBackgroundImage(preview, input.value);
    },
    toggleBorder: function () { C.setBorder(!S().border); },
    toggleDetails: function () { C.setDetails(S().details !== "on"); },

    pickType: function (el) {
        if (claimLockedTile(el) || actions.typeSoldOut(el)) return;
        C.selectType(el.getAttribute("data-option"));
    },
    pickSize: function (el) {
        if (claimLockedTile(el)) return;
        C.selectSize(el.getAttribute("data-option"));
    },
    pickFrame: function (el) {
        if (claimLockedTile(el) || actions.frameSoldOut(el)) return;
        C.selectFrame(el.getAttribute("data-option"));
    },
    pickGift: function (el) {
        var map = { no_gift__picker: "none", ribbon_wrap__picker: "ribbon", gift_wrap__picker: "giftwrap", bundle__picker: "bundle" };
        C.setGift(map[el.getAttribute("data-dropdown")] || "none");
    },
    togglePeek: function () {
        S().peekHidden = !S().peekHidden;
        applyPeekTo3D();
    },
    showPeek: function () {
        S().peekHidden = false;
        applyPeekTo3D();
    },

    // ---- bindings ----
    typeActive: function (el) { return S().type === el.getAttribute("data-option"); },
    tileType: function (el) {
        var w = el.closest("[data-type]");
        return w ? w.getAttribute("data-type") : null;
    },
    // Each type's size / frame grid keeps its own selection while hidden, so
    // switching back to a type shows what it had.
    sizeActive: function (el) {
        var type = actions.tileType(el);
        return (S().sel[type] || {}).size === el.getAttribute("data-option");
    },
    frameActive: function (el) {
        var type = actions.tileType(el);
        return C.hasFrameChoice(type) && (S().sel[type] || {}).frame === el.getAttribute("data-option");
    },
    // Availability is worked out for the type's current frame, exactly as the
    // old availability pass did for the visible grid.
    sizeOk: function (el) {
        var type = actions.tileType(el);
        S().sel; // track selection changes
        return C.sizeAvailable(type, el.getAttribute("data-option"));
    },
    // Sold out = made in this type (and frame) but out of stock; the tile
    // shows a "Sold out" pill (customizer-shared.css .cust-soldout).
    sizeSoldOut: function (el) {
        if (claimActive() || actions.sizeOk(el)) return false;
        var type = actions.tileType(el), size = el.getAttribute("data-option");
        if (C.isUkOnlySize(type, size)) return false;
        return C.sizeMade(type, size, C.hasFrameChoice(type) ? (S().sel[type] || {}).frame : null);
    },
    frameSoldOut: function (el) {
        if (claimActive()) return false;
        var type = actions.tileType(el);
        var size = (S().sel[type] || {}).size;
        return !!size && C.frameSoldOut(type, size, el.getAttribute("data-option"));
    },
    typeSoldOut: function (el) {
        return !claimActive() && C.typeSoldOut(el.getAttribute("data-option"));
    },
    soldOutLabel: function (el, soldOut) {
        return soldOut ? el.getAttribute("data-option") + ", sold out" : null;
    },
    frameStyle: function (el) {
        if (claimActive()) return actions.lockStyle();
        return actions.frameSoldOut(el) ? { pointerEvents: "none", cursor: "not-allowed" } : {};
    },
    typeStyle: function (el) {
        if (claimActive()) return actions.lockStyle();
        return actions.typeSoldOut(el) ? { pointerEvents: "none", cursor: "not-allowed" } : {};
    },
    sizePrice: function (p) {
        var el = p.closest("[data-option]");
        if (actions.sizeSoldOut(el)) return "\u00a0";
        var type = actions.tileType(el);
        var sel = S().sel[type] || {};
        var frame = C.hasFrameChoice(type) ? sel.frame : null;
        if (C.isUkOnlySize(type, el.getAttribute("data-option"))) return "UK delivery only";
        var v = C.sizeVariant(type, el.getAttribute("data-option"), frame);
        return v ? C.formatMoney(v.price, window.currencyFormat) : "Sold Out";
    },
    // Claim mode: product / size / frame are locked to the paid variant.
    // <div> tiles can't take `disabled`, so the lock is aria-disabled +
    // tabindex -1 + no pointer events, and the pick actions refuse too.
    claimLocked: function () { return claimActive(); },
    lockStyle: function () {
        return claimActive() ? { opacity: "0.55", pointerEvents: "none", cursor: "not-allowed" } : {};
    },
    sizeStyle: function (el) {
        if (claimActive()) return actions.lockStyle();
        if (actions.sizeOk(el)) return {};
        if (actions.sizeSoldOut(el)) return { pointerEvents: "none", cursor: "not-allowed" };
        return { opacity: "0.5", pointerEvents: "none", cursor: "not-allowed" };
    },
    stateVisible: function (el) {
        var type = el.getAttribute("data-type");
        // The Liquid loop also renders an empty-type wrapper; never show it.
        if (!type || S().type !== type) return false;
        if (el.classList.contains("state_3")) return C.hasFrameChoice(type);
        return true;
    },
    giftActive: function (el) {
        var map = { no_gift__picker: "none", ribbon_wrap__picker: "ribbon", gift_wrap__picker: "giftwrap", bundle__picker: "bundle" };
        return S().gift.selectedOption === map[el.getAttribute("data-dropdown")];
    },
    isPrintType: function () { S().type; return window.moonIsPrintType(); },
    anyWrap: function () { var g = S().gift; return g.ribbon || g.giftWrap || g.bundle; },
    bgTileActive: function (label) {
        var input = document.getElementById(label.getAttribute("for"));
        return !!input && S().bgImage === input.value;
    },
    fontActive: function (font) {
        var s = S();
        var target;
        if (s.fontScope === "details") target = s.detailsFont;
        else if (s.fontScope === "message") target = s.messageFont;
        else target = (s.messageFont === s.detailsFont) ? s.messageFont : null;
        return target !== null && target === font;
    },
    onFontClick: function (e) {
        var scopeBtn = e.target.closest(".sm2-font-scope-btn[data-scope]");
        if (scopeBtn) { S().fontScope = scopeBtn.getAttribute("data-scope"); return; }
        var btn = e.target.closest(".sm2-font-btn[data-font]");
        if (btn) applyFont(btn.getAttribute("data-font"));
    },

    // ---- steps ----
    goStep: function (i) { goStep(i, { scroll: true }); },

    // ---- modals ----
    closeWelcome: function () { UI().welcomeOpen = false; },
    closeInfo: function () { UI().infoOpen = false; },
    closeSave: function () { UI().saveOpen = false; },
    sendSave: function () { sendSaveForLater(); },
    closeSuccess: function () { closeSuccess(); },
    closeEmoji: function () { UI().emojiOpen = false; },
    emojis: function () { return EMOJIS[UI().emojiCategory] || []; },
    pickEmoji: function (emoji) { insertEmoji(emoji); },
    closeInspiration: function () { UI().inspOpen = false; },
    inspirationItems: function () { return INSPIRATION[UI().inspCategory] || []; },
    inspirationHTML: function (text) { return "<p>" + text.replace(/\n/g, "<br>") + "</p>"; },
    applyInspiration: function () {
        var sel = UI().inspSelected;
        if (sel) {
            var input = document.getElementById("custom_message_input");
            if (input) { input.value = sel; fire(input, "input"); }
        }
        UI().inspOpen = false;
    },

    // ---- text inputs ----
    messageKeydown: function (e) {
        if (e.key === "Enter" && countMoonMessageLines(e.target.value + "\n") > MAX_MESSAGE_LINES) e.preventDefault();
    },
    messageInput: function (e) { onMessageInput(e.target); },
    captionKeydown: function (e) {
        var el = e.target;
        var selectionLength = el.selectionEnd - el.selectionStart;
        var controlKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Tab", "Home", "End"];
        if (el.value.length >= MAX_CAPTION_CHARS && selectionLength === 0 && controlKeys.indexOf(e.key) === -1) e.preventDefault();
    },
    captionInput: function (e) {
        var el = e.target;
        var caption = el.value || "";
        var trimmed = caption.slice(0, MAX_CAPTION_CHARS);
        if (trimmed !== caption) el.value = trimmed;
        qa("#skyMap").forEach(function (s) { s.textContent = trimmed; });
    },
};

function claimLockedTile(el) {
    if (window.isMoonClaimLocked && window.isMoonClaimLocked() && el.closest("[data-claim-locked]")) {
        console.log("[claim] blocked: picker is locked to the paid variant");
        return true;
    }
    return false;
}

document.addEventListener("alpine:init", function () {
    window.Alpine.store("moonUi", uiState);
    // A fresh object per x-data root: Alpine defines its magics ($nextTick,
    // ...) on the data object, so sharing one would throw on the second root.
    window.Alpine.data("moonUi", function () { return Object.assign({}, actions); });
});

// ============================================
// FONTS
// ============================================
function applyFont(fn) {
    var scope = S().fontScope;
    if (scope === "message" || scope === "both") C.setMessageFont(fn);
    if (scope === "details" || scope === "both") C.setDetailsFont(fn);
}
// The toolbar's Font button cycles through the grid for the current scope.
function cycleFont() {
    var tiles = qa("#moon-font-grid .sm2-font-btn[data-font]");
    if (!tiles.length) return;
    var list = tiles.map(function (t) { return t.getAttribute("data-font"); });
    var s = S();
    var current = (s.fontScope === "details") ? s.detailsFont : s.messageFont;
    applyFont(list[(list.indexOf(current) + 1) % list.length]);
}
window.__moonFontPicker = { cycle: cycleFont };

// ============================================
// MESSAGE + CAPTION
// ============================================
var MAX_MESSAGE_LINES = 5;
var MESSAGE_CHARS_PER_LINE = 60;
var MAX_CAPTION_CHARS = 70;
// The textarea value is NEVER rewritten while typing: the preview and the
// print both word-wrap the message as HTML, so free text is safe, and
// explicit Enter breaks are kept as <br>. This soft-wrap estimate only
// backs the Enter guard.
function countMoonMessageLines(s) {
    if (!s) return 0;
    return s.split(/\r?\n/).reduce(function (n, line) {
        return n + Math.max(1, Math.ceil(line.length / MESSAGE_CHARS_PER_LINE));
    }, 0);
}
// Long-line nudge, same threshold + wording as the star map.
var MSG_WARN_CHARS = 55;
function updateMoonMessageNudge() {
    var input = document.getElementById("custom_message_input");
    var hint = document.getElementById("moon-message-hint");
    if (!input || !hint) return;
    var warn = (input.value || "").split("\n").some(function (line) { return line.length > MSG_WARN_CHARS; });
    input.classList.toggle("sm2-input--warn", warn);
    hint.classList.toggle("sm2-hint--warn", warn);
    hint.textContent = warn
        ? "Long line — your printed version may wrap differently from the preview. Press Enter to break where you want."
        : "Up to 5 lines — press Enter to break where you want.";
}
function onMessageInput(input) {
    var message = input.value || "";
    // Paste guard only: the Enter guard can't stop a multi-line paste, so
    // collapse explicit newlines beyond the cap into spaces.
    var parts = message.split(/\r?\n/);
    if (parts.length > MAX_MESSAGE_LINES) {
        message = parts.slice(0, MAX_MESSAGE_LINES - 1).concat(parts.slice(MAX_MESSAGE_LINES - 1).join(" ")).join("\n");
        input.value = message;
    }
    C.renderMessage(message);
    updateMoonMessageNudge();
}

// Preset messages for the toolbar's Text button.
var PRESET_MESSAGES = [
    "The Moon Was Just Like This\nThe Night We Met",
    "Our Love Shines\nAs Bright As The Moon",
    "Under This Moon\nForever And Always",
    "The night we fell in love\n🌙❤️",
    "To the moon and back",
    "Our love story under the moon",
    "Forever under this moon",
    "The Moon Remembers\nOur Perfect Moment",
    "Where our love began...",
    "The Night Everything Changed",
    "The moon witnessed our love",
    "Our magical moment",
    "Together beneath the moon",
    "This is where our story begins"
];
var currentMessageIndex = 0;
function rotateMessage() {
    currentMessageIndex = (currentMessageIndex + 1) % PRESET_MESSAGES.length;
    var next = PRESET_MESSAGES[currentMessageIndex];
    var input = document.getElementById("custom_message_input");
    if (input) input.value = next;
    qa(".custom-textarea").forEach(function (el) { el.textContent = next; });
    fire(input, "input");
}

// ============================================
// PRINTED-TEXT OVERRIDES + LOCATION MIRROR
// ============================================
function wireTextInputs() {
    var initialLocationDisplay = (q("#custom_add") || {}).textContent || "";
    var initialDate = (q("#custom_date") || {}).textContent || "";
    var initialCoords = (q("#custom_lat_lng") || {}).textContent || "";
    if (!(q("#custom_date_text") || {}).value) setValue("#custom_date_text", initialDate);
    if (!(q("#custom_lat_long") || {}).value) setValue("#custom_lat_long", initialCoords);
    if (!(q("#custom_address") || {}).value) setValue("#custom_address", initialLocationDisplay);

    // The hidden #address_form1 mirror: the Places selection writes it and
    // fires change. The printed location follows it, and a real place
    // (not the London default) satisfies the Add-to-Cart gate.
    function onLocation(e) {
        var locationText = (e.target.value || "").trim();
        var displayText = locationText || initialLocationDisplay;
        setText("#custom_add", displayText);
        setValue("#custom_address", displayText);
        // A new place re-derives the printed-location override.
        window.moonAddressOverrideCleared = false;
        if (locationText && locationText !== initialLocationDisplay && locationText !== window.initialLocationDefault) {
            window.userHasEnteredLocation = true;
        } else if ((!locationText || locationText === initialLocationDisplay) && !window.userHasEnteredLocation) {
            // Never flip it off once the autocomplete has set it (a picked
            // "London, UK" is a real choice).
            window.userHasEnteredLocation = false;
        }
    }
    on("#address_form1", "input", onLocation);
    on("#address_form1", "change", onLocation);

    // Printed location. Deliberately emptied → the line prints blank (print
    // text only; it must not unset the moon's location or the cart gate).
    on("#custom_address", "input", function (e) {
        var customAddr = (e.target.value || "").trim();
        window.moonAddressOverrideCleared = customAddr.length === 0;
        setText("#custom_add", customAddr || String.fromCharCode(160));
        if (customAddr) {
            setValue("#address_form1", customAddr);
            if (customAddr !== initialLocationDisplay && customAddr !== window.initialLocationDefault) {
                window.userHasEnteredLocation = true;
            }
        }
    });
    // Printed date. Once typed, the auto dateline stops following the
    // pickers (moon-generator.js reads the dirty flag); emptying it prints a
    // blank line.
    on("#custom_date_text", "input", function (e) {
        var customDate = (e.target.value || "").trim();
        window.moonDateOverrideDirty = customDate.length > 0;
        window.moonDateOverrideCleared = customDate.length === 0;
        setText("#custom_date", customDate || String.fromCharCode(160));
    });
    on("#custom_lat_long", "input", function (e) {
        var customCoords = (e.target.value || "").trim();
        window.moonCoordsOverrideDirty = customCoords.length > 0;
        window.moonCoordsOverrideCleared = customCoords.length === 0;
        setText("#custom_lat_lng", customCoords || String.fromCharCode(160));
    });

    // Any date select change counts as the customer choosing a date.
    ["change"].forEach(function (type) {
        document.addEventListener(type, function (e) {
            if (e.target && e.target.matches && e.target.matches(".bear-dates, .bear-months, .bear-years")) {
                window.userHasSelectedDate = true;
            }
        });
    });

    // Autosave on every edit, like the old delegated listeners.
    var AUTOSAVE_INPUTS = "#address_form1, #custom_address, #custom_date_text, #custom_lat_long, " +
        ".bear-dates, .bear-months, .bear-years, .moon-hours, .moon-minutes, .moon-meridiem, .custom-textarea, " +
        "#textTitle, #custom_message_input, #moon_time_on, #newsletterSignup";
    ["input", "change"].forEach(function (type) {
        document.addEventListener(type, function (e) {
            if (e.target && e.target.matches && e.target.matches(AUTOSAVE_INPUTS)) C.scheduleAutosave();
        });
    });
}
function on(sel, type, fn) { qa(sel).forEach(function (el) { el.addEventListener(type, fn); }); }
function setValue(sel, v) { qa(sel).forEach(function (el) { el.value = v; }); }
function setText(sel, v) { qa(sel).forEach(function (el) { el.textContent = v; }); }

// ============================================
// STEPS
// ============================================
var stepScrollRequest = 0;
function scrollMoonStepIntoView() {
    if (!window.matchMedia || !window.matchMedia("(max-width: 991px)").matches) return;
    var side = document.querySelector(".app__wrapper > .app__side");
    if (!side) return;
    var request = ++stepScrollRequest;
    window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
            if (request !== stepScrollRequest) return;
            // With the sticky preview (moon-stage.js), the sidebar has to land
            // under where the preview rests once pinned, not under the viewport
            // edge. Its `top` is negative (it collapses under the edge), so
            // top + height is the compact height.
            var safeTop = 8;
            var main = document.querySelector(".app__wrapper > .app__main");
            if (main) {
                var ms = window.getComputedStyle(main);
                var pinnedBottom = (parseFloat(ms.top) || 0) + main.getBoundingClientRect().height;
                if (ms.position === "sticky" && pinnedBottom < window.innerHeight * 0.8) safeTop = Math.ceil(pinnedBottom);
            }
            var targetY = Math.max(0, (window.scrollY || window.pageYOffset || 0) + side.getBoundingClientRect().top - safeTop);
            var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            window.scrollTo({ top: targetY, behavior: reducedMotion ? "auto" : "smooth" });
        });
    });
}
function stepCount() { return qa(".cust-step-nav .cust-step-tab").length || 4; }
function goStep(index, opts) {
    opts = opts || {};
    var n = stepCount();
    if (index < 0) index = 0;
    if (index >= n) index = n - 1;
    var prev = S().step;
    S().step = index;
    if (index !== prev && opts.scroll) scrollMoonStepIntoView();
}
// Step badges: step 2 (Moment) only counts once a location and a date are
// set; steps 3 and 4 only count if they were opened after that.
function markStepVisited(stepNum) {
    var u = UI();
    var visited = Object.assign({}, u.visited);
    visited[stepNum] = true;
    u.visited = visited;
    var s = S();
    if (stepNum >= 3 && s.validation.hasLocation && s.validation.hasDate) {
        var completed = Object.assign({}, u.completed);
        completed[stepNum] = true;
        u.completed = completed;
    }
}
window.updateMoonProgress = function () { /* badges are reactive now */ };

function wireSteps() {
    var tabs = qa(".cust-step-nav .cust-step-tab");
    tabs.forEach(function (tab, i) {
        tab.addEventListener("click", function (e) {
            e.preventDefault();
            // Index among the TABS: SkylitTabPill inserts its indicator as the
            // nav's first child.
            goStep(i, { scroll: true });
            markStepVisited(i + 1);
        });
    });
    var next = q(".cust-footer-btn--next");
    var prev = q(".cust-footer-btn--prev");
    if (next) next.addEventListener("click", function (e) {
        e.preventDefault();
        var target = S().step + 1;
        if (target < stepCount()) goStep(target, { scroll: true });
        markStepVisited(Math.min(S().step + 1, 4));
    });
    if (prev) prev.addEventListener("click", function (e) {
        e.preventDefault();
        var target = S().step - 1;
        if (target >= 0) goStep(target, { scroll: true });
    });

    window.Alpine.effect(function () {
        var step = S().step;
        var n = tabs.length;
        tabs.forEach(function (tab, i) {
            tab.classList.toggle("active", i === step);
        });
        var submit = q(".cust-footer-btn--submit");
        var footer = q(".cust-footer");
        var last = step === n - 1;
        if (submit) submit.classList.toggle("show-submit", last);
        if (footer) footer.classList.toggle("on-final-step", last);
        if (prev) prev.disabled = step === 0;
    });

    window.Alpine.effect(function () {
        var u = UI();
        var s = S();
        var step2Complete = s.validation.hasLocation && s.validation.hasDate;
        var completed = u.completed;
        if (step2Complete && !completed[2]) {
            completed = Object.assign({}, completed, { 2: true });
            u.completed = completed;
        }
        qa(".cust-step-nav .cust-step-tab-num").forEach(function (badge, i) {
            var stepNum = i + 1;
            badge.classList.remove("visited", "incomplete");
            if (stepNum === 1) badge.classList.add("visited");
            else if (stepNum === 2) {
                if (step2Complete) badge.classList.add("visited");
                else if (u.visited[2]) badge.classList.add("incomplete");
            } else if (completed[stepNum]) badge.classList.add("visited");
            else if (u.visited[stepNum]) badge.classList.add("incomplete");
        });
    });
}

// ============================================
// FOOTER: price, button label, submit
// ============================================
// The label follows the gate: a location and a date are both needed before
// Add To Cart. Claim mode is decided first and wins over everything else.
function buttonState() {
    var s = S();
    UI().claimTick;
    var hasLoc = s.validation.hasLocation, hasDate = s.validation.hasDate;
    var v = C.currentVariant();
    if (window.SkylitClaim && window.SkylitClaim.active) {
        if (!(window.SkylitClaim.usable && window.SkylitClaim.claim)) {
            return { label: window.SkylitClaim.message || "Unavailable", disabled: true };
        }
        if (hasLoc && hasDate) return { label: "Confirm my design", disabled: false };
    }
    if (v && !v.available) return { label: "Sold Out", soldOut: true };
    var label;
    if (hasLoc && hasDate) label = s.editMode ? "Confirm Edits" : "Add To Cart";
    else if (!hasLoc && !hasDate) label = "Select Location & Date";
    else if (!hasLoc) label = "Select Location";
    else label = "Select Date";
    return { label: label, disabled: window.SkylitClaim && window.SkylitClaim.active ? false : undefined };
}
function paintButton() {
    var st = buttonState();
    var btn = q(".cust-footer-btn--submit");
    var label = q(".cust-footer-btn--submit .cust-footer-btn-label");
    if (label) label.textContent = st.label;
    if (!btn) return;
    if (st.disabled !== undefined) {
        btn.disabled = !!st.disabled;
        btn.setAttribute("aria-disabled", st.disabled ? "true" : "false");
    }
    btn.style.pointerEvents = st.soldOut ? "none" : "";
    btn.style.opacity = st.soldOut ? "0.5" : "";
}
window.updateMoonButton = paintButton;
window.updateAddToCartButtonText = paintButton;
window.updateButtonState = paintButton;

// Relaxed shake for a missing Moment field.
function flashField(el) {
    if (!el || !el.animate) return;
    var shake = ["0px", "-5px", "5px", "-4px", "4px", "-2px", "2px", "0px"];
    el.animate(shake.map(function (x) { return { transform: "translateX(" + x + ")" }; }), { duration: 900, easing: "ease-in-out" });
}

function wireFooter() {
    window.Alpine.effect(function () {
        var total = C.totalPence();
        S().gift.selectedOption; S().type; S().sel;
        var el = q(".cust-footer-price-text");
        if (el && total != null) el.innerHTML = C.formatMoney(total, window.currencyFormat);
        // Wrap/bundle pills are priced by size.
        if (typeof window.refreshMoonGiftLabels === "function") window.refreshMoonGiftLabels();
    });
    window.Alpine.effect(function () {
        var s = S();
        s.validation.hasLocation; s.validation.hasDate; s.editMode; s.variantId; UI().claimTick;
        paintButton();
    });
    document.addEventListener("skylit:claim-ready", function () { UI().claimTick++; });
    // A claim that turns out used / expired / unreachable fires no event:
    // design-claim.js just sets its message. Repaint when that changes so
    // the button explains why it is locked.
    if (window.SkylitClaim && window.SkylitClaim.active) {
        var lastMsg = window.SkylitClaim.message, polls = 0;
        var claimPoll = setInterval(function () {
            var c = window.SkylitClaim;
            if (c.message !== lastMsg || c.usable) { lastMsg = c.message; UI().claimTick++; }
            if (c.usable || ++polls > 50) clearInterval(claimPoll);
        }, 300);
    }

    var submit = q(".cust-footer-btn--submit");
    if (submit) submit.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Mid-add (the button is morphing): ignore repeat taps.
        if (window.SkylitAtcMorph && window.SkylitAtcMorph.active()) return;
        var req = C.checkRequirements();
        if (req.canAddToCart) {
            window.submit_form("");
            return;
        }
        // Take them to the Moment step and shake what's missing.
        goStep(1, { scroll: true });
        markStepVisited(2);
        setTimeout(function () {
            if (!req.hasLocation) flashField(q("#moon-location-wrapper"));
            if (!req.hasDate) flashField(q(".row__dates"));
        }, 300);
    });
}

// ============================================
// GIFT OPTIONS: overlays, peek, 3D, add-on pickers
// ============================================
window.onMoonGiftChange = function (option, opts) {
    renderOverlays();
    if (opts && opts.degrade) return;
    // 3D preview follows the wrapping (bundle = paper + ribbon + bow).
    var framed = window.Moon3DFramedMockup;
    if (framed && typeof framed.showRibbon === "function") {
        if (window.ribbonSelected) framed.showRibbon(); else framed.hideRibbon();
    }
    if (framed && typeof framed.showGiftWrap === "function") {
        if (window.giftWrapSelected) {
            framed.showGiftWrap();
            if (typeof framed.setWrapDesign === "function") framed.setWrapDesign(window.moonGiftPaper || "dots");
        } else {
            framed.hideGiftWrap();
        }
    }
    var canvas = window.Moon3DMockup;
    if (canvas && typeof canvas.showRibbon === "function") {
        if (window.ribbonSelected) canvas.showRibbon(); else canvas.hideRibbon();
    }
};

// Peeking hides the 3D ribbon + wrap parcel too; un-peeking restores
// whatever is selected.
function applyPeekTo3D() {
    renderOverlays();
    var api = window.Moon3DFramedMockup;
    if (!api) return;
    if (window.moonGiftPeekHidden) {
        if (typeof api.hideRibbon === "function") api.hideRibbon();
        if (typeof api.hideGiftWrap === "function") api.hideGiftWrap();
    } else {
        if (typeof api.showRibbon === "function" && window.ribbonSelected) api.showRibbon();
        if (typeof api.showGiftWrap === "function" && window.giftWrapSelected) {
            api.showGiftWrap();
            if (typeof api.setWrapDesign === "function") api.setWrapDesign(window.moonGiftPaper || "dots");
        }
    }
}

// Overlay geometry. The wrap / ribbon images are absolutely-positioned
// siblings inside #print (not children of .custom-frame, which has overflow
// rules of its own), so their box is measured from the frame every time the
// frame changes.
// The frame's border-box size from its computed style: fractional, and
// unaffected by transforms (the same numbers jQuery's outerWidth gave).
function outerSize(el) {
    var cs = getComputedStyle(el);
    var w = parseFloat(cs.width) || 0, h = parseFloat(cs.height) || 0;
    if (cs.boxSizing !== "border-box") {
        w += (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0) + (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0);
        h += (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    }
    return { w: w, h: h };
}
function moonOverlayGeometry() {
    var frame = q(".custom-frame");
    var print = q("#print");
    if (!frame || !print) return null;
    var fr = frame.getBoundingClientRect();
    var pr = print.getBoundingClientRect();
    var size = outerSize(frame);
    // The wood frame is drawn as ::after with inset:-22px; with a real frame,
    // grow the overlays to its outer edge so the gift paper covers the whole
    // gifted poster instead of stopping at the print area.
    var hasWoodFrame = /\bframe-(black|white|natural|dark-brown)\b/.test(frame.className);
    var framePad = hasWoodFrame ? 22 : 0;
    var relativeTop = fr.top - pr.top;
    var relativeLeft = fr.left - pr.left;
    // The preview stage (moon-stage.js) scales #print as the phone preview
    // collapses and lays it into a photo in Room view; on-screen rects are
    // transformed then, so measure the layout offsets instead.
    if (print.style.transform || print.style.scale || print.style.translate) {
        relativeTop = frame.offsetTop + frame.clientTop;
        relativeLeft = frame.offsetLeft + frame.clientLeft;
    }
    return {
        frameWidth: size.w,
        frameHeight: size.h,
        relativeTop: relativeTop,
        relativeLeft: relativeLeft,
        top: relativeTop - framePad,
        left: relativeLeft - framePad,
        width: size.w + framePad * 2,
        height: size.h + framePad * 2
    };
}
// Cheap re-measure of the overlays already on screen (no teardown, so it
// can run every frame of a transition). Selection changes go through
// renderOverlays().
window.repositionMoonOverlays = function () {
    var g = moonOverlayGeometry();
    if (!g) return;
    qa(".gift-wrap-overlay, .ribbon-overlay").forEach(function (el) {
        el.style.top = g.top + "px";
        el.style.left = g.left + "px";
        el.style.width = g.width + "px";
        el.style.height = g.height + "px";
    });
    var giftCard = document.getElementById("moon-giftcard-overlay");
    if (giftCard && giftCard.classList.contains("visible")) {
        var cardW = g.frameWidth * 0.38;
        giftCard.style.top = (g.relativeTop + g.frameHeight * 0.55) + "px";
        giftCard.style.left = (g.relativeLeft + (g.frameWidth - cardW) / 2) + "px";
        giftCard.style.width = cardW + "px";
    }
};
// .custom-frame reshapes over 300ms when the size changes; a
// ResizeObserver catches every intermediate frame (and viewport resizes).
var _moonFrameRO = null;
function moonObserveFrame() {
    if (typeof ResizeObserver === "undefined") return;
    var frameEl = q(".custom-frame");
    if (!frameEl) return;
    if (_moonFrameRO) _moonFrameRO.disconnect();
    _moonFrameRO = new ResizeObserver(function () { window.repositionMoonOverlays(); });
    _moonFrameRO.observe(frameEl);
}

function overlayImg(cls, src, g, z, extra) {
    var img = document.createElement("img");
    img.className = cls;
    img.setAttribute("src", src);
    img.style.position = "absolute";
    img.style.top = g.top + "px";
    img.style.left = g.left + "px";
    img.style.width = g.width + "px";
    img.style.height = g.height + "px";
    img.style.pointerEvents = "none";
    // Above the caption plaque, which is z=52 in the header composition;
    // mirrors the star customizer's .sm2-gift-overlay { z-index: 95 }.
    img.style.zIndex = z;
    Object.keys(extra || {}).forEach(function (k) { img.style[k] = extra[k]; });
    return img;
}
function renderOverlays() {
    var ribbon = window.ribbonSelected, wrap = window.giftWrapSelected, bundle = window.bundleSelected;
    var peek = window.moonGiftPeekHidden;
    var geo = moonOverlayGeometry();
    var print = q("#print");
    qa(".gift-wrap-overlay, .ribbon-overlay").forEach(function (el) { el.remove(); });
    if (!geo || !print) return;
    moonObserveFrame();
    print.style.position = "relative";
    if (!peek && (wrap || bundle)) {
        var paperSrc = window.SkylitGifts ? window.SkylitGifts.designMeta(window.moonGiftPaper || "dots").texture
            : "https://cdn.shopify.com/s/files/1/0710/5278/7987/files/Skylit-studio-wrapping-paper-5.webp?v=1762717492";
        print.appendChild(overlayImg("gift-wrap-overlay", paperSrc, geo, 94, { opacity: "0.9", objectFit: "cover" }));
    }
    if (!peek && (ribbon || bundle)) {
        // Per-colour ribbon PNG (same 612x765 geometry as the old red one).
        var ribbonSrc = window.SkylitGifts ? window.SkylitGifts.ribbonImageFor(window.moonRibbonColor || "Red")
            : "https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-GIFT-WRAP.png?v=1748258725";
        print.appendChild(overlayImg("ribbon-overlay", ribbonSrc, geo, 95));
    }
    // Gift card over the lower-middle of the frame with a ribbon or bundle.
    var giftCard = document.getElementById("moon-giftcard-overlay");
    if (giftCard) {
        if (!peek && (ribbon || bundle)) {
            if (giftCard.parentElement !== print) print.appendChild(giftCard);
            var cardW = geo.frameWidth * 0.38;
            giftCard.style.top = (geo.relativeTop + geo.frameHeight * 0.55) + "px";
            giftCard.style.left = (geo.relativeLeft + (geo.frameWidth - cardW) / 2) + "px";
            giftCard.style.width = cardW + "px";
            giftCard.classList.add("visible");
        } else {
            giftCard.classList.remove("visible");
        }
    }
}
window.renderOverlays = renderOverlays;

var moonPaperPicker = null;
var moonRibbonPicker = null;
function wireGiftAddons() {
    if (!window.SkylitGifts) return;
    moonPaperPicker = window.SkylitGifts.bindPaperPicker(document.getElementById("moon-gift-paper"), function (id) {
        window.moonGiftPaper = id;
        window.SkylitGifts.rememberPaper("moon_" + C.orderId(), id);
        renderOverlays();
        // Repaint an already-open 3D parcel so it matches the 2D preview.
        if (window.Moon3DFramedMockup && typeof window.Moon3DFramedMockup.setWrapDesign === "function") {
            window.Moon3DFramedMockup.setWrapDesign(id);
        }
    });
    if (moonPaperPicker) moonPaperPicker.set(window.moonGiftPaper);
    moonRibbonPicker = window.SkylitGifts.bindRibbonPicker(document.getElementById("moon-gift-ribbon"), function (name) {
        window.moonRibbonColor = name;
        window.SkylitGifts.rememberRibbon("moon_" + C.orderId(), name);
        renderOverlays();
    });
    if (moonRibbonPicker) moonRibbonPicker.set(window.moonRibbonColor);
}
// A reopened design brings its own paper / ribbon memory.
window.onMoonGiftAddonsRestored = function () {
    if (moonPaperPicker) moonPaperPicker.set(window.moonGiftPaper);
    if (moonRibbonPicker) moonRibbonPicker.set(window.moonRibbonColor);
};

// A variant change moves or reshapes the frame: re-place the overlays once
// the frame classes have settled.
window.onMoonVariantChange = function () {
    setTimeout(renderOverlays, 50);
};

// ============================================
// INITIAL SELECTION, ?variant= DEEP LINK, CLAIM MODE
// ============================================
function variantFromUrl() {
    try {
        var urlVid = new URLSearchParams(window.location.search).get("variant");
        if (!urlVid || !window.productVariants) return null;
        return window.productVariants.find(function (v) { return String(v.id) === String(urlVid); }) || null;
    } catch (e) { return null; }
}
function applyVariant(v) {
    var s = S();
    var cur = C.currentSelection();
    if (cur.type !== v.option1) C.selectType(v.option1);
    if (C.currentSelection().size !== v.option2) C.selectSize(v.option2);
    if (v.option3 && C.hasFrameChoice(v.option1) && C.currentSelection().frame !== v.option3) C.selectFrame(v.option3);
    return s.type === v.option1;
}
function setInitialSelections() {
    // Google Merchant Center links carry ?variant=<advertised offer id>.
    // Select it so the advertised variant's price is what renders (MC's
    // automated-discounts review checks it).
    var uv = variantFromUrl();
    if (uv && applyVariant(uv)) {
        console.log("🔗 URL variant preselected:", uv.option1, "/", uv.option2, "/", uv.option3 || "—");
        // Re-assert for 20s until the shopper interacts (late boot work used
        // to stomp the selection); applyVariant only acts on a mismatch.
        var reassert = setInterval(function () { applyVariant(uv); }, 800);
        var stop = function () { clearInterval(reassert); };
        document.addEventListener("pointerdown", stop, { once: true, capture: true });
        setTimeout(stop, 20000);
        return;
    }
    // Default: Print / Unframed (Framed Black A4 used to be the default and
    // went out of stock), then Framed, then the first type.
    var types = C.productTypes();
    var def = types.find(function (t) { var l = t.toLowerCase(); return l.indexOf("print") !== -1 || l.indexOf("unframed") !== -1; }) ||
        types.find(function (t) { var l = t.toLowerCase(); return l.indexOf("framed") !== -1 || l.indexOf("black") !== -1; }) ||
        types[0];
    if (def) C.selectType(def);
}

// Claim mode: design-claim.js sets SkylitClaim.active synchronously, then
// resolves the paid variant and dispatches skylit:claim-ready. Pre-select it
// (exempt from the lock) whenever that lands relative to our boot.
var moonBooted = false;
var pendingMoonClaim = null;
function applyMoonClaimVariant(claim) {
    var norm = function (t) { return (t || "").replace(" - Sold Out", "").trim().toLowerCase(); };
    var type = C.productTypes().find(function (t) { return norm(t) === norm(claim.type); });
    if (!type) return;
    C.setClaimPreselect(true);
    try {
        if (S().type !== type) C.selectType(type, { force: true });
        if (claim.size) {
            var size = C.sizesFor(type).find(function (sz) { return norm(sz) === norm(claim.size); });
            if (size && C.currentSelection().size !== size) C.selectSize(size, { force: true });
        }
        if (claim.frame && claim.frame !== "No Frame" && C.hasFrameChoice(type)) {
            var frame = C.framesFor(type).find(function (f) { return norm(f) === norm(claim.frame); });
            if (frame && C.currentSelection().frame !== frame) C.selectFrame(frame, { force: true });
        }
    } finally {
        C.setClaimPreselect(false);
    }
}
document.addEventListener("skylit:claim-ready", function (e) {
    var claim = e.detail || {};
    if (moonBooted) applyMoonClaimVariant(claim);
    else pendingMoonClaim = claim;
});

// ============================================
// DATE + TIME PICKERS (SkylitWheel modals)
// ============================================
var MONTHS = ["January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"];
var moonSelectedMonth = new Date().getMonth();
var moonSelectedDay = new Date().getDate();
var moonSelectedYear = new Date().getFullYear();
var moonDateWheels = null;
var moonSelectedHour = 11;
var moonSelectedMinute = 11;
var moonSelectedMeridiem = "PM";
var moonTimeWheels = null;

function moonDayItems() {
    var n = new Date(moonSelectedYear, moonSelectedMonth + 1, 0).getDate();
    if (moonSelectedDay > n) moonSelectedDay = n;
    var items = [];
    for (var d = 1; d <= n; d++) items.push(d);
    return items;
}
function populateMoonDays() {
    if (moonDateWheels) moonDateWheels.day.setItems(moonDayItems(), moonSelectedDay);
}
function populateMoonDateWheels() {
    var monthWheel = document.getElementById("moonMonthWheel");
    var dayWheel = document.getElementById("moonDayWheel");
    var yearWheel = document.getElementById("moonYearWheel");
    if (!monthWheel || !dayWheel || !yearWheel || !window.SkylitWheel) return;
    if (moonDateWheels) {
        moonDateWheels.month.setValue(moonSelectedMonth);
        moonDateWheels.year.setValue(moonSelectedYear);
        populateMoonDays();
        return;
    }
    var confirm = function () { confirmDate(); };
    var currentYear = new Date().getFullYear();
    var years = [];
    for (var y = currentYear - 105; y <= currentYear + 10; y++) years.push(y);
    moonDateWheels = {
        month: window.SkylitWheel.create(monthWheel, {
            itemClass: "date-picker-item", label: "Month",
            items: MONTHS.map(function (m, i) { return { value: i, label: m }; }), value: moonSelectedMonth,
            onChange: function (v) { moonSelectedMonth = parseInt(v); populateMoonDays(); }, onEnter: confirm
        }),
        day: window.SkylitWheel.create(dayWheel, {
            itemClass: "date-picker-item", label: "Day", items: moonDayItems(), value: moonSelectedDay,
            onChange: function (v) { moonSelectedDay = parseInt(v); }, onEnter: confirm
        }),
        year: window.SkylitWheel.create(yearWheel, {
            itemClass: "date-picker-item", label: "Year", typeable: true, items: years, value: moonSelectedYear,
            onChange: function (v) { moonSelectedYear = parseInt(v); populateMoonDays(); }, onEnter: confirm
        })
    };
}
// Once the modal has a size, centre the wheels and hand the keyboard to the
// first one.
function syncMoonWheels(set, first) {
    if (!set) return;
    requestAnimationFrame(function () {
        Object.keys(set).forEach(function (k) { set[k].sync(); });
        try { if (first) first.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    });
}
function updateMoonDatePickerDisplay() {
    var display = document.getElementById("moonDatePickerDisplay");
    if (display) display.textContent = moonSelectedDay + " " + MONTHS[moonSelectedMonth] + " " + moonSelectedYear;
}
// The hidden selects are what moon-generator.js computes the moon from;
// "change" on the year drives the recompute.
function syncMoonDateToSelects() {
    setValue(".bear-dates", moonSelectedDay);
    setValue(".bear-months", moonSelectedMonth);
    setValue(".bear-years", moonSelectedYear);
    fire(q(".bear-years"), "change");
}
function readMoonDateFromSelects() {
    var d = q(".bear-dates"), m = q(".bear-months"), y = q(".bear-years");
    if (d && d.value) moonSelectedDay = parseInt(d.value) || moonSelectedDay;
    if (m && m.value !== "") moonSelectedMonth = parseInt(m.value);
    if (y && y.value) moonSelectedYear = parseInt(y.value) || moonSelectedYear;
}
function openDate() {
    readMoonDateFromSelects();
    populateMoonDateWheels();
    UI().dateOpen = true;
    syncMoonWheels(moonDateWheels, document.getElementById("moonMonthWheel"));
}
function confirmDate() {
    updateMoonDatePickerDisplay();
    syncMoonDateToSelects();
    UI().dateOpen = false;
    window.userHasSelectedDate = true;
}

function populateMoonTimeWheels() {
    var hourWheel = document.getElementById("moonHourWheel");
    var minuteWheel = document.getElementById("moonMinuteWheel");
    var meridiemWheel = document.getElementById("moonMeridiemWheel");
    if (!hourWheel || !minuteWheel || !meridiemWheel || !window.SkylitWheel) return;
    if (moonTimeWheels) {
        moonTimeWheels.hour.setValue(moonSelectedHour);
        moonTimeWheels.minute.setValue(moonSelectedMinute);
        moonTimeWheels.meridiem.setValue(moonSelectedMeridiem);
        return;
    }
    var confirm = function () { confirmTime(); };
    var hours = [];
    for (var h = 1; h <= 12; h++) hours.push(h);
    var minutes = [];
    for (var m = 0; m < 60; m++) minutes.push({ value: m, label: m.toString().padStart(2, "0") });
    moonTimeWheels = {
        hour: window.SkylitWheel.create(hourWheel, {
            itemClass: "time-picker-item", label: "Hour", items: hours, value: moonSelectedHour,
            onChange: function (v) { moonSelectedHour = parseInt(v); }, onEnter: confirm
        }),
        minute: window.SkylitWheel.create(minuteWheel, {
            itemClass: "time-picker-item", label: "Minute", items: minutes, value: moonSelectedMinute,
            onChange: function (v) { moonSelectedMinute = parseInt(v); }, onEnter: confirm
        }),
        meridiem: window.SkylitWheel.create(meridiemWheel, {
            itemClass: "time-picker-item", label: "AM or PM", items: ["AM", "PM"], value: moonSelectedMeridiem,
            onChange: function (v) { moonSelectedMeridiem = v; }, onEnter: confirm
        })
    };
}
function updateMoonTimePickerDisplay() {
    var display = document.getElementById("moonTimePickerDisplay");
    if (display) display.textContent = moonSelectedHour + ":" + moonSelectedMinute.toString().padStart(2, "0") + " " + moonSelectedMeridiem;
}
function syncMoonTimeToSelects() {
    setValue(".moon-hours", moonSelectedHour);
    setValue(".moon-minutes", moonSelectedMinute);
    setValue(".moon-meridiem", moonSelectedMeridiem);
    fire(q(".moon-meridiem"), "change");
}
function readMoonTimeFromSelects() {
    var h = q(".moon-hours"), m = q(".moon-minutes"), mer = q(".moon-meridiem");
    if (h && h.value !== "") moonSelectedHour = parseInt(h.value) || 9;
    if (m && m.value !== "") moonSelectedMinute = parseInt(m.value) || 0;
    if (mer && mer.value !== "") moonSelectedMeridiem = mer.value || "PM";
}
function openTime() {
    readMoonTimeFromSelects();
    populateMoonTimeWheels();
    UI().timeOpen = true;
    syncMoonWheels(moonTimeWheels, document.getElementById("moonHourWheel"));
}
function confirmTime() {
    updateMoonTimePickerDisplay();
    syncMoonTimeToSelects();
    UI().timeOpen = false;
}
Object.assign(actions, {
    openDate: openDate,
    confirmDate: confirmDate,
    cancelDate: function () { UI().dateOpen = false; },
    openTime: openTime,
    confirmTime: confirmTime,
    cancelTime: function () { UI().timeOpen = false; },
});

function wirePickers() {
    // Modal open = page scroll locked. Only touch body overflow when a
    // modal opens or closes, never on boot.
    var scrollLocked = false;
    window.Alpine.effect(function () {
        var u = UI();
        var locked = !!(u.dateOpen || u.timeOpen || u.successOpen);
        if (locked === scrollLocked) return;
        scrollLocked = locked;
        document.body.style.overflow = locked ? "hidden" : "";
    });
    if (window.SkylitWheel) {
        ["#moonDatePickerDone", "#moonDatePickerConfirm", "#moonTimePickerDone", "#moonTimePickerConfirm"].forEach(function (sel) {
            var b = q(sel);
            if (b) window.SkylitWheel.hapticButton(b);
        });
    }
    // The trigger labels follow the hidden selects (the saved-order loader
    // and the cookie restore set them and fire change).
    document.addEventListener("change", function (e) {
        var t = e.target;
        if (!t || !t.matches) return;
        if (t.matches(".bear-dates, .bear-months, .bear-years")) { readMoonDateFromSelects(); updateMoonDatePickerDisplay(); }
        if (t.matches(".moon-hours, .moon-minutes, .moon-meridiem")) { readMoonTimeFromSelects(); updateMoonTimePickerDisplay(); }
    });
    // First paint, once moon-generator.js has populated the selects, and
    // again after the cookie restore.
    [800, 1500].forEach(function (ms) {
        setTimeout(function () {
            readMoonDateFromSelects(); updateMoonDatePickerDisplay();
            readMoonTimeFromSelects(); updateMoonTimePickerDisplay();
        }, ms);
    });

    // ?prefill_date=YYYY-MM-DD from a date search on /search; ?prefill_lat/
    // lng/place from the cart's cross-sell rail (the star map's place).
    try {
        var p = new URLSearchParams(window.location.search);
        if (p.get("order_id") || p.get("saved") || p.get("preset")) return;
        var pd = p.get("prefill_date");
        if (pd && /^\d{4}-\d{2}-\d{2}$/.test(pd)) {
            var pa = pd.split("-");
            var py = parseInt(pa[0], 10), pmo = parseInt(pa[1], 10) - 1, pdy = parseInt(pa[2], 10);
            var chk = new Date(Date.UTC(py, pmo, pdy));
            if (chk.getUTCMonth() === pmo && chk.getUTCDate() === pdy) {
                // After the moon engine + selects have booted.
                setTimeout(function () {
                    moonSelectedYear = py;
                    moonSelectedMonth = pmo;
                    moonSelectedDay = pdy;
                    updateMoonDatePickerDisplay();
                    syncMoonDateToSelects();
                    window.userHasSelectedDate = true;
                    console.log("[moon] prefill_date=" + pd);
                }, 600);
            }
        }
        var pLat = parseFloat(p.get("prefill_lat"));
        var pLng = parseFloat(p.get("prefill_lng"));
        if (isFinite(pLat) && isFinite(pLng) && pLat >= -90 && pLat <= 90 && pLng >= -180 && pLng <= 180) {
            var pPlace = (p.get("prefill_place") || "").slice(0, 80);
            var tries = 0;
            var timer = setInterval(function () {
                tries++;
                if (window.setMoonLocation) {
                    clearInterval(timer);
                    window.setMoonLocation(pLat, pLng, pPlace || undefined);
                    // A named place is a real location choice, exactly like a
                    // Places pick: fill the printed location and open the
                    // Add-to-Cart gate. (Coordinates alone would print the
                    // London placeholder, so they don't count.)
                    if (pPlace) {
                        window.moonAddressOverrideCleared = false;
                        window.moonCoordsOverrideCleared = false;
                        window.userHasEnteredLocation = true;
                        var mirror = document.getElementById("address_form1");
                        if (mirror) { mirror.value = pPlace; fire(mirror, "change"); }
                    }
                    console.log("[moon] prefill_location=" + pLat + "," + pLng + " " + pPlace);
                } else if (tries > 20) {
                    clearInterval(timer);
                }
            }, 500);
        }
    } catch (e) { console.log("[moon] prefill error", e); }
}

// ============================================
// SAVE FOR LATER + SUCCESS NOTIFICATION
// ============================================
function shakeInput(input, keepRed) {
    if (!input) return;
    input.style.borderColor = "#e74c3c";
    if (!keepRed) input.style.animation = "shake 0.5s ease";
    setTimeout(function () {
        input.style.animation = "";
        input.style.borderColor = "rgba(195, 156, 113, 0.3)";
    }, keepRed ? 2000 : 500);
}
function sendSaveForLater() {
    var input = document.getElementById("saveEmail");
    var email = input ? input.value.trim() : "";
    var newsletter = !!(q("#newsletterSignup") && q("#newsletterSignup").checked);
    if (!email) { shakeInput(input, false); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { shakeInput(input, true); return; }
    (window.SkylitEmailCheck ? window.SkylitEmailCheck(email) : Promise.resolve({ ok: true })).then(function (chk) {
        var errEl = document.getElementById("saveEmailCheckError");
        if (!chk.ok) {
            shakeInput(input, true);
            if (!errEl && input) {
                errEl = document.createElement("p");
                errEl.id = "saveEmailCheckError";
                errEl.style.cssText = "color:#e74c3c;font-size:14px;margin:8px 0 0;font-weight:500;";
                input.insertAdjacentElement("afterend", errEl);
            }
            if (errEl) { errEl.textContent = chk.message; errEl.style.display = "block"; }
            return;
        }
        if (errEl) errEl.style.display = "none";
        window.moonNewsletterSignup = newsletter;
        window.submit_form("?send_email=true");
    });
}
window.onMoonSavedForLater = function (saveRes, email, newsletter) {
    UI().saveOpen = false;
    if (saveRes && saveRes.status === 429) {
        UI().saveOpen = true;
        var errEl = document.getElementById("saveRateLimitError");
        if (!errEl) {
            errEl = document.createElement("p");
            errEl.id = "saveRateLimitError";
            errEl.style.cssText = "color:#e74c3c;font-size:14px;text-align:center;margin:10px 0 0;font-weight:500;";
            var footer = document.querySelector("#saveModalOverlay .save-modal-footer");
            if (footer) footer.appendChild(errEl);
        }
        errEl.textContent = "You've sent too many save requests. Please try again later.";
        errEl.style.display = "block";
    } else {
        showSuccess(email, newsletter);
    }
    var nl = q("#newsletterSignup");
    if (nl) nl.checked = false;
};
function showSuccess(email, showNewsletter) {
    var u = UI();
    u.saveOpen = false;
    u.successEmail = email || "";
    u.successNewsletter = !!(showNewsletter || window.moonNewsletterSignup);
    u.successOpen = true;
}
function closeSuccess() {
    UI().successOpen = false;
    window.moonNewsletterSignup = false;
}
window.showMoonSuccessNotification = showSuccess;
window.closeMoonSuccessNotification = closeSuccess;
window.closeWelcomeMessage = function () { UI().welcomeOpen = false; };
window.reopenWelcomeMessage = function () { UI().welcomeOpen = true; };

// ============================================
// EMOJI PICKER + MESSAGE INSPIRATION
// ============================================
var EMOJIS = {
    smileys: ["😀", "😃", "😄", "😁", "😊", "😍", "🥰", "😘", "😗", "😙", "😚", "☺️", "😌", "😉", "😋", "😎", "🤗", "🤩", "🥳", "😏", "😬", "😐", "😑", "😶", "🙄", "😴"],
    nature: ["🌟", "⭐", "✨", "💫", "🌙", "🌛", "🌜", "🌝", "🌞", "☀️", "🌈", "☁️", "⛅", "🌤", "🌥", "🌦", "🌧", "⛈", "🌩", "🌨", "❄️", "☃️", "⛄", "🌬", "💨", "🌪"],
    symbols: ["💖", "💗", "💓", "💞", "💕", "💘", "💝", "💟", "❤️", "🧡", "💛", "💚", "💙", "💜", "🤎", "🖤", "🤍", "💯", "💢", "💥", "💫", "💦", "💨", "🕳", "💬", "👁️‍🗨️"],
    activities: ["🎉", "🎊", "🎈", "🎀", "🎁", "🎂", "🎄", "🎃", "🎆", "🎇", "🧨", "✨", "🎋", "🎍", "🎎", "🎏", "🎐", "🎑", "🧧", "🎗", "🎟", "🎫", "🎖", "🏆", "🏅", "🥇"]
};
function insertEmoji(emoji) {
    var input = document.getElementById("custom_message_input");
    if (input) {
        var pos = input.selectionStart;
        input.value = input.value.substring(0, pos) + emoji + input.value.substring(pos);
        input.focus();
        input.selectionStart = input.selectionEnd = pos + emoji.length;
        fire(input, "input");
    }
    UI().emojiOpen = false;
}
var INSPIRATION = {
    anniversary: [
        "Under this moon, our love story continues.\nForever and always.",
        "The night we fell in love,\nthe stars aligned just for us.",
        "Every year together is another\nchapter written in the stars.",
        "To the moon and back,\nand then back to the moon again.",
        "Our love began under this moon.\nIt will last for eternity."
    ],
    love: [
        "I love you to the moon and back.",
        "You are my sun, my moon,\nand all my stars.",
        "The moon is a reminder\nthat even in darkness, there is light.",
        "When I look at the moon,\nI think of you.",
        "Our love shines brighter\nthan a thousand moons."
    ],
    baby: [
        "The moon shone a little brighter\nthe night you came into our world.",
        "Born under this moon,\nloved for all time.",
        "A new star was born\nunder this beautiful moon.",
        "The night you arrived,\nthe whole world smiled.",
        "Welcome to the world, little one.\nThe moon welcomed you too."
    ],
    birthday: [
        "Another trip around the sun,\nanother year under the moon.",
        "The moon witnessed your arrival,\nand celebrates every year since.",
        "Happy Birthday!\nMay your year shine as bright as this moon.",
        "Born under a lucky moon,\nblessed with another beautiful year."
    ],
    wedding: [
        "Under this moon, we said \"I do.\"\nOur forever starts here.",
        "The night we became one,\nthe moon was our witness.",
        "Two hearts, one moon,\na lifetime of love.",
        "Married under the glow of this moon.\nBlessed for eternity.",
        "The moon saw our vows\nand sealed them with its light."
    ],
    family: [
        "Family: where life begins\nand love never ends.",
        "Bound by love,\nlit by the moon.",
        "Our family tree grows\nunder this watchful moon.",
        "Together is our favorite place to be.",
        "Home is wherever we're together."
    ],
    memorial: [
        "Forever in our hearts,\nforever watching from above.",
        "The moon remembers\nthe ones we love.",
        "Gone from our sight,\nbut never from our hearts.",
        "You left footprints on our hearts\nthat will never fade.",
        "We look at this moon\nand know you're looking too."
    ],
    poetic: [
        "The moon is a loyal companion.\nIt never leaves.",
        "We are all made of stardust\nand moonbeams.",
        "The moon whispers secrets\nto those who listen.",
        "In the silence of the night,\nthe moon speaks volumes.",
        "Dance with the moon,\ndream with the stars."
    ]
};
Object.assign(actions, {
    openEmoji: function () { UI().emojiOpen = true; },
    openInspiration: function () {
        var u = UI();
        u.inspCategory = "anniversary";
        u.inspSelected = null;
        u.inspOpen = true;
    },
    pickInspirationCategory: function (cat) {
        UI().inspCategory = cat;
        UI().inspSelected = null;
    },
});

// ============================================
// TOOLBAR, INFO POPUP, ESCAPE, FAB, HEADER OFFSET
// ============================================
function wireToolbar() {
    document.addEventListener("click", function (e) {
        var t = e.target;
        if (!t.closest) return;
        if (t.closest("#moonInfoBtn")) { e.stopPropagation(); UI().infoOpen = !UI().infoOpen; return; }
        if (t.closest(".floating-message-btn")) { e.preventDefault(); rotateMessage(); return; }
        if (t.closest(".floating-font-btn")) { e.preventDefault(); cycleFont(); return; }
        if (t.closest(".save-for-later-btn, .Click-here")) { e.preventDefault(); UI().saveOpen = true; return; }
        // Click anywhere outside the info popup closes it.
        var popup = document.getElementById("moonInfoPopup");
        if (UI().infoOpen && popup && !popup.contains(t)) UI().infoOpen = false;
    });
    document.addEventListener("keydown", function (e) {
        if (e.key !== "Escape") return;
        var u = UI();
        if (u.infoOpen) u.infoOpen = false;
        if (u.saveOpen) u.saveOpen = false;
        if (u.inspOpen) u.inspOpen = false;
    });
    var infoBtn = document.getElementById("moonInfoBtn");
    if (infoBtn) {
        infoBtn.setAttribute("aria-haspopup", "dialog");
        infoBtn.setAttribute("aria-controls", "moonInfoPopup");
        window.Alpine.effect(function () {
            var open = UI().infoOpen;
            infoBtn.classList.toggle("active", open);
            infoBtn.setAttribute("aria-expanded", open ? "true" : "false");
        });
    }

    // Mobile: the toolbar collapses behind a bottom-right button (star map's
    // bindToolbarFab). Desktop never shows the button (CSS, >=992px).
    var main = q(".app__main");
    var fab = document.getElementById("moonToolbarFab");
    var toolbar = document.getElementById("moonToolbar");
    if (main && fab && toolbar) {
        var isOpen = function () { return main.classList.contains("app__main--tools-open"); };
        var onOutside = function (e) {
            if (fab.contains(e.target) || toolbar.contains(e.target)) return;
            setOpen(false);
        };
        var onKey = function (e) {
            if (e.key !== "Escape") return;
            setOpen(false);
            fab.focus();
        };
        var setOpen = function (open) {
            main.classList.toggle("app__main--tools-open", open);
            fab.setAttribute("aria-expanded", open ? "true" : "false");
            fab.setAttribute("aria-label", open ? "Hide tools" : "Show tools");
            if (open) {
                document.addEventListener("pointerdown", onOutside, true);
                document.addEventListener("keydown", onKey);
            } else {
                document.removeEventListener("pointerdown", onOutside, true);
                document.removeEventListener("keydown", onKey);
            }
        };
        fab.addEventListener("click", function () { setOpen(!isOpen()); });
        // Info and Save open a modal, so the flyout has done its job. Text and
        // Font get tapped repeatedly — leave those up.
        [".floating-info-btn", ".floating-save-btn"].forEach(function (sel) {
            var btn = toolbar.querySelector(sel);
            if (btn) btn.addEventListener("click", function () { setOpen(false); });
        });
    }

    function setMoonHeaderOffset() {
        var header = document.querySelector("header, .header-wrapper, .shopify-section-header");
        var announcement = document.querySelector(".announcement-bar, .shopify-section-announcement-bar, .announcement-bar-section");
        var offset = 0;
        if (header) offset += header.offsetHeight;
        else if (announcement) offset += announcement.offsetHeight;
        if (header && announcement && !header.contains(announcement)) offset += announcement.offsetHeight;
        var wrap = document.querySelector(".app__wrapper");
        if (wrap) wrap.style.setProperty("--moon-header-offset", offset + "px");
    }
    setTimeout(setMoonHeaderOffset, 100);
    window.addEventListener("resize", function () { setTimeout(setMoonHeaderOffset, 50); });
}

// Hover-only tooltips flash and vanish on phones (WebKit drops simulated
// :hover when the tile's click mutates the DOM), so tapping the "?" toggles
// .tooltip-open. Capture phase, so the tap opens the tooltip without also
// selecting the tile.
document.addEventListener("click", function (e) {
    var icon = e.target.closest ? e.target.closest(".tooltip-icon") : null;
    var open = document.querySelectorAll(".tooltip-icon.tooltip-open");
    var i;
    if (!icon) {
        for (i = 0; i < open.length; i++) open[i].classList.remove("tooltip-open");
        return;
    }
    e.stopPropagation();
    var wasOpen = icon.classList.contains("tooltip-open");
    for (i = 0; i < open.length; i++) open[i].classList.remove("tooltip-open");
    if (!wasOpen) icon.classList.add("tooltip-open");
}, true);

// ============================================
// MOON DATA - four-corner astronomical readouts
// ============================================
// Computed client-side from the chosen moment: SunCalc (already loaded for
// the phase engine) for illumination, age, distance and alt/az, plus
// astronomy-engine for the moon's ecliptic longitude (the moon sign).
// Refreshed on the generator's `moon:updated` event.
(function () {
    var SYNODIC = 29.530588853;
    var LUNATION_EPOCH = 947182440000; // 2000-01-06 18:14 UTC = start of Brown lunation 953
    var SIGNS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra",
                 "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];
    var POINTS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
                  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
    function moonEclipticLongitude(date) {
        try {
            if (typeof Astronomy !== "undefined" && Astronomy.EclipticGeoMoon) return Astronomy.EclipticGeoMoon(date).lon;
        } catch (e) {}
        var d = date.getTime() / 86400000 - 10957.5; // days since J2000
        var rad = Math.PI / 180;
        return (218.316 + 13.176396 * d) + 6.289 * Math.sin((134.963 + 13.064993 * d) * rad);
    }
    function put(id, text) {
        var el = document.getElementById(id);
        if (el) el.textContent = text;
    }
    window.updateMoonDataCorners = function () {
        var frame = document.querySelector(".custom-frame");
        if (!frame || frame.getAttribute("data-details") !== "on") return;
        if (typeof SunCalc === "undefined" || typeof window.getMoonCurrentDate !== "function") return;
        var date = window.getMoonCurrentDate();
        var loc = window.moonCurrentLocation || { lat: 51.5074, lng: -0.1278 };
        var illum = SunCalc.getMoonIllumination(date);
        var pos = SunCalc.getMoonPosition(date, loc.lat, loc.lng);
        var lon = ((moonEclipticLongitude(date) % 360) + 360) % 360;
        var altDeg = pos.altitude * 180 / Math.PI;
        var azDeg = ((pos.azimuth * 180 / Math.PI) + 180) % 360; // SunCalc azimuth is from south
        put("md-phase", (window.moonPhaseInfo && window.moonPhaseInfo.phaseName) || "—");
        put("md-illum", Math.round(illum.fraction * 100) + "%");
        put("md-age", (illum.phase * SYNODIC).toFixed(1) + " days");
        put("md-lunation", "No. " + (Math.floor((date.getTime() - LUNATION_EPOCH) / (SYNODIC * 86400000)) + 953));
        put("md-sign", SIGNS[Math.floor(lon / 30) % 12]);
        put("md-distance", Math.round(pos.distance).toLocaleString("en-GB") + " km");
        put("md-alt", (altDeg < 0 ? "−" : "+") + Math.abs(altDeg).toFixed(1) + "°");
        put("md-az", azDeg.toFixed(1) + "° " + POINTS[Math.round(azDeg / 22.5) % 16]);
    };
    document.addEventListener("moon:updated", window.updateMoonDataCorners);
})();

// ============================================
// 3D VIEW (lazy-loaded on first use)
// ============================================
(function () {
    var canvasScriptLoaded = false;
    var framedScriptLoaded = false;
    var isLoading = false;

    function getCurrentDisplayType() {
        var t = String(S().type || "").toLowerCase();
        if (t.indexOf("canvas") !== -1) return "canvas";
        if (t.indexOf("print") !== -1) return "print";
        return "framed";
    }
    function is3DActive() {
        return (window.Moon3DMockup && window.Moon3DMockup.isActive && window.Moon3DMockup.isActive()) ||
               (window.Moon3DFramedMockup && window.Moon3DFramedMockup.isActive && window.Moon3DFramedMockup.isActive());
    }
    function loadScript(url, api) {
        return new Promise(function (resolve) {
            var script = document.createElement("script");
            script.src = url;
            script.onload = async function () {
                await new Promise(function (r) { setTimeout(r, 100); });
                if (window[api]) await window[api].init();
                resolve();
            };
            script.onerror = function () { resolve(); };
            document.head.appendChild(script);
        });
    }
    function loadCanvasScript() {
        if (canvasScriptLoaded) return Promise.resolve();
        return loadScript(window.moon3DMockupUrl, "Moon3DMockup").then(function () { canvasScriptLoaded = true; });
    }
    function loadFramedScript() {
        if (framedScriptLoaded) return Promise.resolve();
        return loadScript(window.moon3DFramedMockupUrl, "Moon3DFramedMockup").then(function () { framedScriptLoaded = true; });
    }
    function label(btn, text) { var l = btn && btn.querySelector(".moon-3d-label"); if (l) l.textContent = text; }

    // Framed/Canvas pill shown during the 3D view. Switching it switches the
    // product type too.
    function createTypeSwitcher() {
        if (document.getElementById("moon-3d-type-switcher")) return;
        var switcher = document.createElement("div");
        switcher.id = "moon-3d-type-switcher";
        switcher.className = "moon-3d-type-switcher";
        ["framed", "canvas"].forEach(function (type) {
            var b = document.createElement("button");
            b.textContent = type === "framed" ? "Framed" : "Canvas";
            b.dataset.type = type;
            switcher.appendChild(b);
        });
        switcher.addEventListener("click", async function (e) {
            var btn = e.target.closest("button");
            if (!btn || btn.classList.contains("active") || isLoading) return;
            var targetType = btn.dataset.type;
            var toggleBtn = document.getElementById("moon-3d-toggle-btn");
            isLoading = true;
            if (targetType === "canvas") await loadCanvasScript();
            else await loadFramedScript();
            await switchTo3DView(targetType, toggleBtn);
            switcher.querySelectorAll("button").forEach(function (b) { b.classList.remove("active"); });
            btn.classList.add("active");
            var type = C.productTypes().find(function (t) { return t.toLowerCase().indexOf(targetType) !== -1; });
            if (type) C.selectType(type);
            isLoading = false;
        });
        var appMain = q(".app__main");
        if (appMain) appMain.appendChild(switcher);
    }
    function showTypeSwitcher(activeType) {
        var switcher = document.getElementById("moon-3d-type-switcher");
        if (!switcher) return;
        switcher.classList.add("visible");
        switcher.querySelectorAll("button").forEach(function (b) { b.classList.toggle("active", b.dataset.type === activeType); });
    }
    function hideTypeSwitcher() {
        var switcher = document.getElementById("moon-3d-type-switcher");
        if (switcher) switcher.classList.remove("visible");
    }
    async function switchTo3DView(type, btn) {
        if (type === "canvas") {
            if (window.Moon3DFramedMockup && window.Moon3DFramedMockup.isActive && window.Moon3DFramedMockup.isActive()) await window.Moon3DFramedMockup.hide();
            if (window.Moon3DMockup) {
                await window.Moon3DMockup.show();
                if (btn) { btn.classList.add("active"); label(btn, "Flat View"); }
            }
        } else {
            if (window.Moon3DMockup && window.Moon3DMockup.isActive && window.Moon3DMockup.isActive()) await window.Moon3DMockup.hide();
            if (window.Moon3DFramedMockup) {
                await window.Moon3DFramedMockup.show();
                if (btn) { btn.classList.add("active"); label(btn, "Flat View"); }
            }
        }
        showTypeSwitcher(type === "canvas" ? "canvas" : "framed");
    }
    async function hideAll3D(btn) {
        if (window.Moon3DMockup && window.Moon3DMockup.isActive && window.Moon3DMockup.isActive()) await window.Moon3DMockup.hide();
        if (window.Moon3DFramedMockup && window.Moon3DFramedMockup.isActive && window.Moon3DFramedMockup.isActive()) await window.Moon3DFramedMockup.hide();
        if (btn) { btn.classList.remove("active"); label(btn, "3D View"); }
        hideTypeSwitcher();
    }
    async function busy(btn, fn) {
        if (btn) { btn.disabled = true; btn.style.opacity = "0.6"; label(btn, "Loading..."); }
        try { await fn(); } catch (e) { console.error("Failed to load 3D preview:", e); }
        if (btn) { btn.disabled = false; btn.style.opacity = "1"; }
    }

    function createToggle() {
        if (document.getElementById("moon-3d-toggle-btn")) return;
        createTypeSwitcher();
        var btn = document.createElement("button");
        btn.id = "moon-3d-toggle-btn";
        btn.className = "moon-3d-toggle-btn";
        btn.innerHTML = '<span class="moon-3d-label">3D View</span><span class="moon-3d-toggle-switch"></span>';
        btn.title = "Toggle 3D Preview";
        btn.addEventListener("click", async function () {
            if (isLoading) return;
            if (is3DActive()) { await hideAll3D(btn); return; }
            isLoading = true;
            var displayType = getCurrentDisplayType();
            await busy(btn, async function () {
                if (displayType === "canvas") await loadCanvasScript();
                else await loadFramedScript();
                await switchTo3DView(displayType, btn);
            });
            isLoading = false;
        });
        var printArea = document.getElementById("print");
        if (printArea && printArea.parentNode) printArea.parentNode.insertBefore(btn, printArea);

        // Print has no 3D view: hide the toggle (and any open 3D view); a
        // type change while 3D is open switches to that type's 3D view.
        var lastType = null;
        window.Alpine.effect(function () {
            var type = S().type;
            var displayType = getCurrentDisplayType();
            btn.style.display = displayType === "print" ? "none" : "flex";
            if (type === lastType) return;
            lastType = type;
            setTimeout(async function () {
                if (displayType === "print") {
                    if (is3DActive()) await hideAll3D(btn);
                    return;
                }
                if (!is3DActive()) return;
                await busy(btn, async function () {
                    if (displayType === "canvas") await loadCanvasScript();
                    else await loadFramedScript();
                });
                await switchTo3DView(displayType, btn);
            }, 150);
        });
    }
    window.moonCreate3DToggle = createToggle;
})();

// ============================================
// BOOT
// ============================================
function boot() {
    if (!window.Alpine) { console.error("[moon-ui] Alpine did not load"); return; }
    // Paint the preview's starting look from the state: no border, the
    // default fonts, and the pre-checked Galaxy background.
    C.setBorder(false);
    C.setMessageFont(S().messageFont);
    C.setDetailsFont(S().detailsFont);
    var checkedBg = q('input[name="background-img"]:checked');
    if (checkedBg) actions.pickBgImage(checkedBg);
    wireTextInputs();
    wireSteps();
    wireFooter();
    wirePickers();
    wireToolbar();
    wireGiftAddons();
    setInitialSelections();
    // Claim mode: lock once the pickers exist (the bindings do the visual
    // lock); if the claim resolved while we were booting, apply it now.
    moonBooted = true;
    if (pendingMoonClaim) {
        applyMoonClaimVariant(pendingMoonClaim);
        pendingMoonClaim = null;
    }
    C.fadeLoader(false);
    // Overlays follow the frame's size, the wrapping, the paper and the peek.
    window.Alpine.effect(function () {
        var s = S();
        s.gift.selectedOption; s.giftPaper; s.ribbonColor; s.peekHidden;
        renderOverlays();
    });
    moonObserveFrame();
    setTimeout(window.moonCreate3DToggle, 500);

    // Shared motion (skylit-motion.js): sliding step-tab pill and the
    // handwriting sliders' fill / grab / rubber-band ends.
    if (window.SkylitTabPill) {
        qa(".cust-step-nav").forEach(function (nav) {
            window.SkylitTabPill.attach(nav, { tab: ".cust-step-tab", className: "cust-steps-indicator" });
        });
    }
    if (window.SkylitSlider) {
        ["sm2-hw-width", "sm2-hw-modal-width", "sm2-hw-size", "sm2-hw-height"].forEach(function (id) {
            window.SkylitSlider.enhance(document.getElementById(id));
        });
    }
    console.log("🌙 Moon Map UI ready");
}
// Back from the cart restores this page as it left it, mid-"Added": put the
// add-to-basket button back.
window.addEventListener("pageshow", function (e) {
    if (e.persisted && window.SkylitAtcMorph) window.SkylitAtcMorph.reset(true);
});

// Deferred scripts run while readyState is already "interactive", before
// alpine.min.js has executed, so wait for DOMContentLoaded (Alpine has
// started by then, and moon-generator.js has built the date selects).
var booted = false;
function bootOnce() { if (booted) return; booted = true; boot(); }
if (document.readyState === "complete") bootOnce();
else {
    document.addEventListener("DOMContentLoaded", bootOnce);
    window.addEventListener("load", bootOnce);
}

})();
